import { useContext, useEffect, useState } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  format,
  parseISO,
  differenceInYears,
  addMinutes,
  isWithinInterval,
} from 'date-fns'
// switch to this when calendar ids are fixed
import { apiClient } from 'utils/api/apiClient'

import Box from '@mui/material/Box'
import {
  Button,
  Select,
  Grid,
  IconButton,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material'
// import Grid2 from '@mui/material/Grid2'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import Typography from '@mui/material/Typography'

import google from 'assets/images/logos/google_meets.png'
import teams from 'assets/images/logos/ms_teams.png'
import zoom from 'assets/images/logos/zoom.png'

// Soft UI Dashboard PRO React components
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import OnboardingMultiOptionTile from '../OnboardingMultiOptionTile'
import { selectData } from 'layouts/onboarding'
import { callZapier } from 'utils/zapier/callZapier'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowBack,
  CheckCircle,
  Close,
  East,
  Flag,
  Toll,
} from '@mui/icons-material'
import deskChair from 'assets/images/deskandchair.svg'
import vacation from 'assets/images/onboarding/vacation.png'
import Banner from 'atoms/Banner'
import gapGif from 'assets/images/gapAnimation.gif'
import GapLoadingPage from './components/GapLoadingPage'
import { useFormDebounce } from 'hooks/useFormDebounce'

export const RetirementSolver = ({
  closeModal = () => console.log('Schedule Appointment'),
  isAdmin = false,
  isOnPageForm = false,
  parcoReadinessScore4Percent = 0,
  adjustedNetTakeHomeDifferencePARCOGap = 0,
  valueOfInvestableAssets = 0,
  parcoAnnuityRecommendation = {},
  neededForRetirementFull = 0,
  neededForRetirementTrimmed = 0,
  neededForRetirementParco = 0,
}) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const [appointmentConfirmed, setAppointmentConfirmed] = useState(false)
  const [data, setData] = useState(null)
  const [availableDates, setAvailableDates] = useState(null)
  const [error, setError] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [selectedPlatform, setSelectedPlatform] = useState(null)
  const [assistedLiving, setAssistedLiving] = useState(null)
  const [inflation, setInflation] = useState(null)
  const [gapSelection, setGapSelection] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const [availableTimes, setAvailableTimes] = useState([])

  const dollarFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  // Pagination
  const [open, setOpen] = useState(true)
  const [page, setPage] = useState(0)
  const [direction, setDirection] = useState(1)
  const handleNext = () => {
    setDirection(1)
    setPage((prev) => Math.min(prev + 1, pages.length - 1))
  }
  const handleBack = () => {
    setDirection(-1)
    setPage((prev) => Math.max(prev - 1, 0))
  }
  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
    }),
  }

  const interestedClick = () => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/u5dbhzm/',
      payload: {
        user: {
          firstName: intakeState?.firstName,
          lastName: intakeState?.lastName,
          phoneNumber: intakeState?.phone,
          email: intakeState?.email,
          neededForRetirementFull: neededForRetirementFull,
          neededForRetirementTrimmed: neededForRetirementTrimmed,
          neededForRetirementParco: neededForRetirementParco,
        },
      },
      sendNotification: false,
    })
    handleNext()
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({
        clickedRetirementSolverImInterested: true,
      })
    )()
  }

  const dateToYears = (startDate, endDate) => {
    return startDate && endDate
      ? differenceInYears(new Date(endDate), new Date(startDate))
      : ''
  }

  // Eastern Time Formatter
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  const fetchAvailability = async () => {
    const now = new Date()
    const endDate = new Date(now)
    endDate.setMonth(endDate.getMonth() + 2)
    const begin =
      now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate()
    const end =
      endDate.getFullYear() +
      '-' +
      (endDate.getMonth() + 1) +
      '-' +
      endDate.getDate()
    try {
      const calendarIds = []

      // IF HAS PRIMARY STRATEGIST
      if (
        intakeState.retirementStrategist !== '' &&
        intakeState.retirementStrategist
      ) {
        const strategistMatch = selectData.retirementStrategist.find(
          (s) =>
            s.value.toLowerCase().trim() ===
            intakeState.retirementStrategist.toLowerCase().trim()
        )

        if (strategistMatch) {
          // console.log('Times available for ' + strategistMatch.value)
          calendarIds.push(strategistMatch.id)
        } else {
          console.warn(
            'Strategist not found in selectData:',
            intakeState.retirementStrategist
          )
        }
      } else {
        if (dateToYears(intakeState?.dateOfBirth, new Date()) > 59) {
          //quinn
          // console.log('times avaiable for quinn')

          calendarIds.push(selectData.retirementStrategist[2].id)
        } else if (
          intakeState.agency ===
            'u.s._agency_for_international_development_(usaid)' ||
          intakeState.agency === 'department_of_state_(dos)'
        ) {
          //dan
          // console.log('times avaiable for dan')
          calendarIds.push(selectData.retirementStrategist[1].id)
        } else {
          //dan & john
          // console.log('times avaiable for dan & john')

          calendarIds.push(selectData.retirementStrategist[0].id)
          calendarIds.push(selectData.retirementStrategist[1].id)
        }
      }

      const payload = {
        begin: begin,
        end: end,
        calendar_ids_to_check: calendarIds.join('%2C'),
      }

      const response = await fetch(
        'https://1p9cjflrkb.execute-api.us-east-2.amazonaws.com/api/users/availability',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      )

      const data = await response.json()

      // const { data } = await apiClient.post('/api/users/availability', {
      //   body: payload,
      // })
      setData(data)

      const availableDatesSet = new Set(
        data.availability.map((iso) => iso.split('T')[0])
      )
      setAvailableDates(availableDatesSet)
    } catch (e) {
      console.error('Error fetching availability:', e)
    }
  }

  // console.log(data.availability)

  // Initial fetch of availability data
  useEffect(() => {
    fetchAvailability()
  }, [])

  useEffect(() => {
    setSelectedTime(null) // Reset the selected time when date changes
  }, [selectedDate])

  const getAvailableTimes = (selectedDate) => {
    if (!data?.availability || !selectedDate) return []

    return data.availability
      .filter((isoString) => {
        const datePart = isoString.split('T')[0]
        return datePart === selectedDate
      })
      .map((isoString) => {
        const time = formatter.format(new Date(isoString))
        return { time: time, timeISO: isoString }
      })
  }

  // Update available times when the selected date changes
  useEffect(() => {
    if (!selectedDate || !data) return
    setAvailableTimes(getAvailableTimes(selectedDate))
  }, [selectedDate, data])

  const scheduleWith = intakeState?.retirementStrategist
    ? selectData.retirementStrategist.find(
        (s) => s.value === intakeState.retirementStrategist
      )?.sfId
    : dateToYears(intakeState?.dateOfBirth, new Date()) > 59
    ? selectData.retirementStrategist[3].sfId +
      ', ' +
      selectData.retirementStrategist[2].sfId
    : intakeState?.agency ===
        'u.s._agency_for_international_development_(usaid)' ||
      intakeState?.agency === 'department_of_state_(dos)'
    ? selectData.retirementStrategist[1].sfId
    : selectData.retirementStrategist[0].sfId +
      ', ' +
      selectData.retirementStrategist[1].sfId

  const clientAge = intakeState?.dateOfBirth
    ? differenceInYears(new Date(), new Date(intakeState?.dateOfBirth))
    : 'Date of Birth Not Provided'

  const sendApptToSchedule = () => {
    const eventToSchedule = {
      firstName: intakeState?.firstName || 'No First Name',
      lastName: intakeState?.lastName || 'No Last Name',
      email: intakeState?.email || 'No Email',
      phone: intakeState?.phone || 'No Phone',
      leadId: intakeState?.salesforceLeadID || 'No Lead ID',
      accountId: intakeState?.salesforceAccountID || 'No Account ID',
      scheduleWith: scheduleWith,
      selectedDate: format(new Date(selectedTime), 'MM/dd/yyyy') || '',
      selectedTime: formatter.format(new Date(selectedTime)) + ' EST' || '',
      dateTimeISO: selectedTime || '',
      selectedPlatform: selectedPlatform || '',
      meetingTypeWithduration:
        selectData.appointmentTypes.find(
          ({ value }) => value === selectedType
        ) || '',
      assistedLiving: assistedLiving,
      inflation: inflation,
      gapSelection: gapSelection,
      parcoReadinessScore4Percent: isNaN(parcoReadinessScore4Percent)
        ? 0
        : parcoReadinessScore4Percent,
      adjustedNetTakeHomeDifferencePARCOGap: isNaN(
        adjustedNetTakeHomeDifferencePARCOGap * 12
      )
        ? 0
        : adjustedNetTakeHomeDifferencePARCOGap * 12,
      investableAssets: isNaN(valueOfInvestableAssets)
        ? 0
        : valueOfInvestableAssets,
      age: clientAge,
    }
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/27b0m1u/',
      payload: {
        eventToSchedule: eventToSchedule,
      },
      notificationMessage: `Your Appointment on ${format(
        new Date(selectedTime),
        'MM/dd/yyyy'
      )} at ${formatter.format(
        new Date(selectedTime)
      )} EST is being scheduled!`,
      sendNotification: false,
    })
    setAppointmentConfirmed(true)
    updateIntakeStateAtKey({ appointmentScheduled: true })
    handleNext()
    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({
        clickedRetirementAndScheduled: true,
      })
    )()
    // closeModal()
  }

  const pages = [
    <Grid key="0" item xs={12} mt={{ xs: 0, sm: 8 }} mb={4} sx={{}}>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }} // stack on mobile
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{
          maxWidth: '1000px',
          margin: 'auto',
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
          gap: { xs: 2, sm: 4 },
        }}
      >
        {/* Left Section */}
        <Box flex={1} maxWidth={{ md: '50%' }}>
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: '48px', sm: '64px' }, color: '#000' }}
          >
            Do more{' '}
          </Typography>
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: '48px', sm: '64px' }, color: '#2370ED' }}
            mb={2}
          >
            with less
          </Typography>

          <Typography variant="body2" sx={{ mb: 3 }}>
            The{' '}
            <Typography
              component="a"
              target="_blank"
              href="https://corporate.vanguard.com/content/dam/corp/research/pdf/sustainable_withdrawal_rates_in_retirement.pdf"
              sx={{ color: '#2370ED', fontWeight: 500 }}
            >
              <u>3% Rule</u>
            </Typography>{' '}
            suggests you need {neededForRetirementFull} to achieve your
            Retirement Lifestyle Goals. Let PARCO help you to do this for a lot
            less.
          </Typography>

          <Button
            variant="parcoRounded"
            onClick={interestedClick}
            sx={{ fontWeight: 600 }}
          >
            I'm interested
          </Button>
        </Box>

        {/* Right Section */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="flex-end"
          gap={{ xs: 4, sm: 8 }}
          flex={1}
          sx={{ mt: { xs: 3, md: 0 } }}
        >
          {/* Without PARCO */}
          <Box textAlign="center">
            <Card
              elevation={0}
              sx={{
                width: 150,
                height: 250,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: '#f9f9f9',
                border: '3px solid #f1f1f1',
                boxShadow: 'none',
              }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" fontWeight="bold" color="#000">
                  {neededForRetirementTrimmed}
                </Typography>
                <Typography variant="body1Medium" color="#75787B">
                  Needed
                </Typography>
              </CardContent>
            </Card>
            <Typography variant="body1Medium" color="#000" component="p" mt={1}>
              Without PARCO
            </Typography>
          </Box>

          {/* With PARCO */}
          <Box textAlign="center">
            <Card
              sx={{
                width: 150,
                height: 160,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: '#E7ECFE',
                border: '3px solid rgba(72,82,194,0.18)',
                boxShadow: 'none',
              }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="#195BC4" fontWeight="bold">
                  {neededForRetirementParco}
                </Typography>
                <Typography variant="body1Medium" color="#2370ED">
                  Needed
                </Typography>
              </CardContent>
            </Card>
            <Typography
              variant="body1Medium"
              color="#195BC4"
              component="p"
              mt={1}
            >
              With PARCO
            </Typography>
          </Box>
        </Box>
      </Box>
    </Grid>,
    <Grid
      key="1"
      item
      xs={12}
      mt={{ xs: 2, sm: 8 }}
      mb={4}
      sx={{
        '& .MuiDayCalendar-weekDayLabel': {
          margin: '0px 0.5vw',
        },
        '& .MuiPickersDay-root': {
          fontWeight: 600,
          margin: '0px 0.5vw',
        },
        '& .MuiPickersDay-root.Mui-disabled': {
          fontWeight: 400,
        },
        '& .MuiPickersDay-root.Mui-selected': {
          backgroundColor: '#0671AD',
        },
        '& .MuiPickersDay-root.Mui-selected:active, & .MuiPickersDay-root.Mui-selected:focus, & .MuiPickersDay-root.Mui-selected:hover':
          {
            backgroundColor: '#045a8a',
          },
        '& .MuiPickersDay-root:hover': {
          backgroundColor: '#e6f0f6',
        },
        '& .MuiPickersCalendarHeader-labelContainer': {
          position: 'absolute',
          left: '50%',
          transform: 'translate(-50%)',
        },
        '& .MuiPickersArrowSwitcher-root': {
          justifyContent: 'space-between',
          width: '100%',
        },
      }}
    >
      <Box sx={{ maxWidth: '650px', margin: 'auto' }}>
        <Box>
          <Typography variant="headerTitle" sx={{ color: '#262627' }}>
            Let's talk about it! When should we meet?
          </Typography>
        </Box>
        <Typography variant="body1" mt={3}>
          You indicated an interest in living the same as you are now, in
          retirement. Ensure this option is right for you, meet with a PARCO
          team member!
        </Typography>
        <DateCalendar
          sx={{ width: '100%' }}
          shouldDisableDate={(date) => {
            const today = new Date()
            today.setHours(0, 0, 0, 0) // Normalize today's date (remove time part)

            const twoMonthsLater = new Date()
            twoMonthsLater.setMonth(today.getMonth() + 2)

            const dateString = format(date, 'yyyy-MM-dd')

            return (
              date < today ||
              date > twoMonthsLater ||
              (availableDates && !availableDates.has(dateString))
            ) // Disable less than today, older than 2 months and dates with no availability
          }}
          value={selectedDate ? parseISO(selectedDate) : null}
          onChange={(newValue) => {
            setSelectedDate(format(newValue, 'yyyy-MM-dd'))

            setSelectedTime(null)
          }}
          views={['day']}
        />
        {selectedDate && (
          <>
            <Typography variant="body1" mb={1}>
              What time works best on the day selected?
            </Typography>
            {renderMuiFormField({
              key: selectedDate,
              autoFocus: false,
              label: 'Select an Available Time',
              placeholder: 'Select a Meeting Timeslot (Eastern Time)',
              inputType: 'select',
              value: selectedTime,
              onChange: (time) => setSelectedTime(time),
              options: availableTimes.map((time) => ({
                value: time.timeISO,
                label: time.time + ' EST',
              })),
            })}
          </>
        )}
      </Box>
    </Grid>,
    <Grid key="2" item mt={{ xs: 2, sm: 8 }} mb={4} xs={12}>
      <Box sx={{ maxWidth: '650px', margin: 'auto' }}>
        <Box mb={1}>
          <Typography variant="headerTitle" sx={{ color: '#262627' }}>
            Which platform would you prefer to meet with?
          </Typography>
        </Box>
        <Typography variant="body1" mt={3}>
          Please select one of the following:
        </Typography>
        <Box>
          <OnboardingMultiOptionTile
            header={' '}
            helpText={''}
            subHeader={' '}
            expectedDataType={'string'}
            tileSize={{ xs: '4' }}
            onChange={(e) => {
              setSelectedPlatform(e)
            }}
            optionArray={[
              {
                optionLabel: 'Google Meet',
                optionValue: 'google_meet_retirement_consultation',
                optionIcon: <img src={google}></img>,
              },
              {
                optionLabel: 'Zoom',
                optionValue: 'zoom_retirement_consultation',
                optionIcon: <img src={zoom}></img>,
              },
              {
                optionLabel: 'Microsoft Teams',
                optionValue: 'ms_teams_retirement_consultation',
                optionIcon: <img src={teams}></img>,
              },
            ]}
          />
          <Box display="flex" justifyContent="space-between">
            <Button
              variant="parcoRounded"
              color="outline"
              onClick={handleBack}
              sx={{ fontWeight: 600 }}
            >
              Back
            </Button>
            <Button
              variant="parcoRounded"
              disabled={!selectedDate || !selectedTime || !selectedPlatform}
              onClick={sendApptToSchedule}
              sx={{
                fontWeight: 600,
              }}
            >
              Schedule Meeting
            </Button>
          </Box>
        </Box>
      </Box>
    </Grid>,
    <Grid key="3" item xs={12} mt={{ xs: 2, sm: 8 }} mb={4}>
      <Box sx={{ maxWidth: '650px', margin: 'auto' }}>
        <Typography variant="headerTitle" sx={{ color: '#262627' }}>
          Meeting scheduled for {format(new Date(selectedTime), 'MM/dd/yyyy')}{' '}
          at {formatter.format(new Date(selectedTime))} EST
        </Typography>
        <Typography variant="body1" mt={3}>
          We can't wait to talk about options for getting you the lifestyle you
          deserve, in retirement! See you soon!
        </Typography>
        <img
          src={vacation}
          style={{
            maxWidth: '400px',
            display: 'block',
            margin: '32px auto',
          }}
        />
      </Box>
    </Grid>,
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          justifyContent: 'space-between',
          position: 'relative',
          display: `${!isOnPageForm ? 'flex' : 'none'}`,
        }}
      >
        {/* {page > 0 && (
          <IconButton
            onClick={handleBack}
            sx={{
              position: 'absolute',
              top: 0,
              left: { xs: -8, sm: -16 },
              color: '#fff',
              zIndex: 1,
              backgroundColor: '#000',
              '&:hover': {
                backgroundColor: '#000',
              },
            }}
          >
            <ArrowBack />
          </IconButton>
        )} */}

        <IconButton
          onClick={closeModal}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            color: '#000',
            zIndex: 1,
            fontSize: '32px',
            backgroundColor: '#fff',
            '&:hover': {
              backgroundColor: '#fff',
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <Grid
        sx={{ textAlign: 'left', overflow: 'hidden' }}
        px={{ xs: 0, sm: 4 }}
        pt={4}
        pb={{ xs: 2, sm: 4 }}
      >
        {/* <motion.div
          animate={{ height }}
          transition={{ duration: 0.3 }}
          style={{ overflow: 'hidden' }}
        > */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={page}
            custom={direction}
            variants={variants}
            // initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            style={{ position: 'relative', width: '100%' }}
          >
            {pages[page]}
            <Box>
              {page === 0 ? (
                <></>
              ) : page === 1 ? (
                <Box
                  sx={{ maxWidth: '650px', margin: 'auto' }}
                  display="flex"
                  justifyContent={'space-between'}
                >
                  <Button
                    variant="parcoRounded"
                    color="outline"
                    onClick={handleBack}
                    sx={{ fontWeight: 600 }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="parcoRounded"
                    onClick={handleNext}
                    disabled={!selectedDate || !selectedTime}
                    sx={{ fontWeight: 600 }}
                  >
                    Continue
                  </Button>
                </Box>
              ) : page < pages.length - 1 ? (
                <></>
              ) : (
                <Box
                  sx={{ maxWidth: '650px', margin: 'auto' }}
                  display="flex"
                  justifyContent={'flex-end'}
                >
                  {!isOnPageForm && (
                    <Button
                      variant="parcoRounded"
                      onClick={closeModal}
                      sx={{ fontWeight: 600 }}
                    >
                      Back to Dashboard
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </motion.div>
        </AnimatePresence>
        {/* </motion.div> */}
      </Grid>
    </LocalizationProvider>
  )
}
